import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FormPageLayout from '../components/common/FormPageLayout';
import Alert from '../components/common/Alert';
import ConfirmModal from '../components/common/ConfirmModal';
import { useToast } from '../context/ToastContext';
import { useConfig } from '../context/ConfigContext';
import { useCreateAccount, useUpdateAccount, useResetAccountBalance, useAccountQuery } from '../hooks/useQueries';
import IconPicker, { getIconComponent } from '../components/common/IconPicker';
// import ColorPicker from '../components/common/ColorPicker';
import CalculatorInput from '../components/common/CalculatorInput';
import { accountAPI } from '../services/api';
import { suggestAccountIconColor } from '../utils/accountSuggestions';
import { Info, IndianRupee, Building2, Wallet, Banknote, CreditCard, MoreHorizontal, Sparkles, Plus } from 'lucide-react';

const ACCOUNT_TYPES = [
  { value: 'bank', label: 'Bank', icon: Building2 },
  { value: 'wallet', label: 'Wallet', icon: Wallet },
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'credit_card', label: 'Cards', icon: CreditCard },
  { value: 'other', label: 'Other', icon: MoreHorizontal }
];

const ACCOUNT_TYPE_LABELS = {
  bank: 'Bank Account',
  wallet: 'Digital Wallet',
  cash: 'Cash',
  credit_card: 'Cards',
  other: 'Other'
};

const initialFormData = {
  name: '',
  type: 'bank',
  description: '',
  icon: '🏦',
  color: '#3b82f6',
  balance: 0
};

export default function AccountFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const { toast } = useToast();
  const { formatAmount, config } = useConfig();
  const createAccountMutation = useCreateAccount();
  const updateAccountMutation = useUpdateAccount();
  const resetBalanceMutation = useResetAccountBalance();

  const { data: accountData } = useAccountQuery(id, { enabled: !!id });
  const account = accountData?.account || accountData;

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);
  const [balanceResetConfirm, setBalanceResetConfirm] = useState({ open: false, newBalance: 0 });
  const [deletedAccountInfo, setDeletedAccountInfo] = useState(null);
  const [checkingDeleted, setCheckingDeleted] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [userOverrodeIcon, setUserOverrodeIcon] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const debounceRef = useRef(null);
  const suggestRef = useRef(null);

  const isEditing = !!id;

  const checkDeletedAccount = useCallback(async (name) => {
    if (!name || name.trim().length < 2 || isEditing) {
      setDeletedAccountInfo(null);
      return;
    }

    setCheckingDeleted(true);
    try {
      const { data } = await accountAPI.checkDeleted(name.trim());
      if (data.data.found) {
        setDeletedAccountInfo(data.data.account);
        setFormData(prev => ({
          ...prev,
          balance: data.data.account.calculatedBalance
        }));
      } else {
        setDeletedAccountInfo(null);
      }
    } catch {
      setDeletedAccountInfo(null);
    } finally {
      setCheckingDeleted(false);
    }
  }, [isEditing]);

  // Auto-suggest icon & color based on name + type
  const runSuggestion = useCallback((name, type) => {
    if (isEditing || userOverrodeIcon) return;
    const result = suggestAccountIconColor(name, type);
    if (result) {
      setSuggestion(result);
      setFormData(prev => ({
        ...prev,
        icon: result.icon,
        color: result.color
      }));
    } else {
      setSuggestion(null);
    }
  }, [isEditing, userOverrodeIcon]);

  useEffect(() => {
    if (account && isEditing) {
      const rawBalance = account.currentBalance ?? account.balance ?? 0;
      const balanceToShow = Math.round(rawBalance * 100) / 100;
      setFormData({
        name: account.name,
        type: account.type,
        description: account.description || '',
        icon: account.icon || '🏦',
        color: account.color || '#3b82f6',
        balance: balanceToShow
      });
      if (account.description) setShowDescription(true);
    }
  }, [account, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (name === 'name') {
      // Debounced deleted account check
      if (!isEditing) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => checkDeletedAccount(value), 500);
      }
      // Debounced icon/color suggestion
      if (suggestRef.current) clearTimeout(suggestRef.current);
      suggestRef.current = setTimeout(() => {
        runSuggestion(value, formData.type);
      }, 300);
    }
  };

  const handleTypeChange = (value) => {
    handleChange({ target: { name: 'type', value } });
    // Re-run suggestion with new type
    if (!isEditing && !userOverrodeIcon) {
      setTimeout(() => runSuggestion(formData.name, value), 50);
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Account name is required';
    } else if (formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      errors.name = 'Name cannot exceed 100 characters';
    }
    if (formData.description && formData.description.length > 200) {
      errors.description = 'Description cannot exceed 200 characters';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const newBalance = formData.balance ? parseFloat(formData.balance) : 0;

    if (isEditing && account.currentBalance !== undefined) {
      const currentBalance = account.currentBalance;
      if (newBalance !== currentBalance) {
        setBalanceResetConfirm({ open: true, newBalance, currentBalance });
        return;
      }
    }

    await saveAccount();
  };

  const saveAccount = async () => {
    setLoading(true);
    setError(null);

    const balanceValue = formData.balance === '' || formData.balance === null || formData.balance === undefined
      ? 0
      : parseFloat(formData.balance);

    const submitData = {
      ...formData,
      balance: isNaN(balanceValue) ? 0 : balanceValue
    };

    try {
      if (isEditing) {
        await updateAccountMutation.mutateAsync({ id, data: submitData });
        toast.success('Account updated successfully');
      } else {
        const result = await createAccountMutation.mutateAsync(submitData);
        if (result.restored) {
          if (result.adjustmentTransaction) {
            toast.success(`"${result.account.name}" restored with ${result.restoredTransactions} transaction(s) and balance adjusted`);
          } else {
            toast.success(`"${result.account.name}" restored with ${result.restoredTransactions} transaction(s)`);
          }
        } else {
          toast.success(`"${result.account?.name || submitData.name}" created successfully`);
        }
      }

      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate('/accounts');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save account');
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceResetConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      await resetBalanceMutation.mutateAsync({
        id,
        newBalance: balanceResetConfirm.newBalance
      });

      const submitData = {
        ...formData,
        balance: account.balance
      };
      await updateAccountMutation.mutateAsync({ id, data: submitData });

      toast.success('Balance adjusted successfully');
      navigate('/accounts');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset balance');
    } finally {
      setLoading(false);
      setBalanceResetConfirm({ open: false, newBalance: 0, currentBalance: 0 });
    }
  };

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate('/accounts');
    }
  };

  // Live preview
  const PreviewIcon = getIconComponent(formData.icon);
  const previewColor = formData.color || '#6366f1';
  const previewBalance = formData.balance === '' || formData.balance === null ? 0 : parseFloat(formData.balance) || 0;

  return (
    <FormPageLayout title={isEditing ? 'Edit Account' : 'Add Account'} onBack={handleBack}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        {/* Live Preview Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-surface-100">
          <p className="text-[10px] font-medium text-surface-400 uppercase tracking-wider mb-3">Preview</p>
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
              style={{ backgroundColor: `${previewColor}15` }}
            >
              <PreviewIcon className="w-6 h-6 transition-colors" style={{ color: previewColor }} fill={previewColor} fillOpacity={0.15} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[15px] text-surface-900 truncate">
                {formData.name || 'Account Name'}
              </p>
              <p className="text-[11px] text-surface-400">
                {ACCOUNT_TYPE_LABELS[formData.type] || formData.type}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="font-extrabold text-lg text-surface-900">
                {formatAmount(previewBalance)}
              </p>
            </div>
          </div>
          {/* Auto-suggest badge inside preview */}
          {suggestion && !isEditing && (
            <div className="flex items-center gap-1.5 mt-2.5 ml-[60px]">
              <Sparkles className="w-3 h-3 text-amber-500" />
              <span className="text-[11px] text-surface-400">Auto-suggested based on name</span>
            </div>
          )}
        </div>

        {/* Account Name */}
        <div>
          <label className="label">Account Name</label>
          <div className="relative">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., HDFC Bank, GPay, Cash"
              enterKeyHint="next"
              className={`input ${validationErrors.name ? 'input-error' : ''}`}
            />
            {checkingDeleted && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-surface-300 border-t-primary-500 rounded-full animate-spin" />
              </div>
            )}
          </div>
          {validationErrors.name && (
            <p className="text-expense-600 text-sm mt-1">{validationErrors.name}</p>
          )}
          {deletedAccountInfo && !isEditing && (
            <div className="flex items-start gap-2 mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Found previously deleted account!</p>
                <p className="mt-1">
                  We found {deletedAccountInfo.transactionCount} transaction(s) with a balance of{' '}
                  <strong>{formatAmount(deletedAccountInfo.calculatedBalance)}</strong>.
                  You can change the Opening Balance below to start with a different amount.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Account Type — inline chips */}
        <div>
          <label className="label">Account Type</label>
          <div className="flex gap-2 flex-wrap">
            {ACCOUNT_TYPES.map(({ value: typeVal, label: typeLabel, icon: TypeIcon }) => {
              const isActive = formData.type === typeVal;
              return (
                <button
                  key={typeVal}
                  type="button"
                  onClick={() => handleTypeChange(typeVal)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                    isActive
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-surface-200 bg-white text-surface-600 hover:border-surface-300'
                  }`}
                >
                  <TypeIcon className="w-4 h-4" />
                  {typeLabel}
                </button>
              );
            })}
          </div>
        </div>

        {/* Balance */}
        <div>
          <label className="label">{isEditing ? 'Balance' : 'Opening Balance'}</label>
          <CalculatorInput
            name="balance"
            value={formData.balance}
            onChange={handleChange}
            placeholder="0.00"
            className="input"
            icon={IndianRupee}
          />
          <p className="text-surface-500 text-xs mt-1">
            {isEditing
              ? 'Changing this will create an adjustment transaction'
              : 'Starting balance to track from'}
          </p>
        </div>

        {/* Icon */}
        <div>
          <label className="label">Icon</label>
          <IconPicker
            value={formData.icon}
            onChange={(icon) => {
              setFormData((prev) => ({ ...prev, icon }));
              setUserOverrodeIcon(true);
            }}
            onColorChange={(color) => setFormData((prev) => ({ ...prev, color }))}
            color={formData.color}
          />
        </div>
        {/* <div>
          <label className="label">Color</label>
          <ColorPicker
            value={formData.color}
            onChange={(color) => {
              setFormData((prev) => ({ ...prev, color }));
              setUserOverrodeIcon(true);
            }}
          />
        </div> */}

        {/* Description — collapsible */}
        {showDescription ? (
          <div>
            <label className="label">Description (Optional)</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={2}
              placeholder="Additional notes about this account..."
              className={`input resize-none ${validationErrors.description ? 'input-error' : ''}`}
              autoFocus={!isEditing}
            />
            {validationErrors.description && (
              <p className="text-expense-600 text-sm mt-1">{validationErrors.description}</p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowDescription(true)}
            className="flex items-center gap-1.5 py-1 text-sm text-primary-500 font-medium"
          >
            <Plus className="w-4 h-4" />
            Add description (optional)
          </button>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-surface-200">
          <button
            type="button"
            onClick={handleBack}
            className="btn-secondary flex-1"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : isEditing ? 'Update' : 'Add Account'}
          </button>
        </div>
      </form>

      {/* Balance Reset Confirmation Modal */}
      <ConfirmModal
        isOpen={balanceResetConfirm.open}
        onClose={() => setBalanceResetConfirm({ open: false, newBalance: 0, currentBalance: 0 })}
        onConfirm={handleBalanceResetConfirm}
        title="Reset Account Balance"
        message={
          <div className="space-y-3">
            <p>You are about to reset the account balance. This will create an adjustment transaction.</p>
            <div className="bg-surface-100 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-surface-600">Current Balance:</span>
                <span className="font-medium">{formatAmount(balanceResetConfirm.currentBalance || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-surface-600">Adjustment:</span>
                <span
                  className="font-medium"
                  style={{ color: (balanceResetConfirm.newBalance - balanceResetConfirm.currentBalance) >= 0 ? config.incomeColor : config.expenseColor }}
                >
                  {formatAmount(Math.abs(balanceResetConfirm.newBalance - balanceResetConfirm.currentBalance))}
                </span>
              </div>
              <div className="flex justify-between text-sm border-t border-surface-200 pt-1 mt-1">
                <span className="text-surface-600">Final Balance:</span>
                <span className="font-semibold">{formatAmount(balanceResetConfirm.newBalance || 0)}</span>
              </div>
            </div>
            <p className="text-sm text-surface-500">Are you sure you want to proceed?</p>
          </div>
        }
        confirmText="Reset Balance"
        variant="warning"
      />
    </FormPageLayout>
  );
}
